import React, { useMemo, useState } from 'react'

import { Icon } from '@/components/Icon/Icon'
import { ImageLightbox } from '@/shared/components/ProductCarousel/ImageLightbox/ImageLightbox'
import { pxToRem } from '@/shared/styles/themes'
import { faStar as faStarLight } from '@fortawesome/pro-light-svg-icons/faStar'
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck'
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons/faStar'
import { Box, Button, Divider, Grid2 as Grid, styled, Typography } from '@mui/material'

import type { ReviewsData } from './types'

const INITIAL_REVIEW_COUNT = 3

interface ProductReviewsSectionProps {
  reviews: ReviewsData
}

export const ProductReviewsSection: React.FC<ProductReviewsSectionProps> = ({ reviews }) => {
  const [expanded, setExpanded] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const visibleReviews = expanded
    ? reviews.featured_reviews
    : reviews.featured_reviews.slice(0, INITIAL_REVIEW_COUNT)

  const reviewPhotos = useMemo(
    () => reviews.featured_reviews
      .map((r) => (r.photo ? { src: r.photo, author: r.author } : null))
      .filter((p): p is { src: string; author: string } => p !== null),
    [reviews.featured_reviews],
  )

  const lightboxImages = useMemo(
    () => reviewPhotos.map(({ src, author }) => ({
      src,
      loading: 'lazy' as const,
      alt: `Review photo by ${author}`,
    })),
    [reviewPhotos],
  )

  const openLightbox = (photoSrc: string) => {
    const index = reviewPhotos.findIndex((p) => p.src === photoSrc)

    if (index === -1) return
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  return (
    <StyledSection>
      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" component="h2" gutterBottom>
        Customer Reviews
      </Typography>

      {/* Rating Summary */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StyledRatingSummary>
            <Typography variant="h2" component="span" fontWeight="bold">
              {reviews.average_rating}
            </Typography>
            <StyledStars>
              {[1, 2, 3, 4, 5].map((star) => (
                <Icon
                  key={star}
                  icon={star <= Math.round(reviews.average_rating) ? faStarSolid : faStarLight}
                  sx={{
                    color: star <= Math.round(reviews.average_rating)
                      ? 'secondary.main'
                      : 'text.disabled',
                    fontSize: pxToRem(20),
                  }}
                />
              ))}
            </StyledStars>
            <Typography variant="body2" color="text.secondary">
              Based on {reviews.total_reviews} reviews
            </Typography>
          </StyledRatingSummary>
        </Grid>

        <Grid size={{ xs: 12, sm: 8 }}>
          <StyledRatingBars>
            {[5, 4, 3, 2, 1].map((rating) => (
              <StyledRatingBarRow key={rating}>
                <Typography variant="body2" sx={{ minWidth: pxToRem(60) }}>
                  {rating} stars
                </Typography>
                <StyledRatingBar>
                  <StyledRatingBarFill
                    $percentage={
                      ((reviews.rating_distribution[String(rating)] || 0) / reviews.total_reviews) * 100
                    }
                  />
                </StyledRatingBar>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: pxToRem(30) }}>
                  {reviews.rating_distribution[String(rating)] || 0}
                </Typography>
              </StyledRatingBarRow>
            ))}
          </StyledRatingBars>
        </Grid>
      </Grid>

      {/* Review List */}
      <Box>
        {visibleReviews.map((review, index) => (
          <React.Fragment key={review.author}>
            {index > 0 && <Divider sx={{ my: 3 }} />}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <StyledStars>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Icon
                      key={star}
                      icon={star <= review.rating ? faStarSolid : faStarLight}
                      sx={{
                        color: star <= review.rating ? 'secondary.main' : 'text.disabled',
                        fontSize: pxToRem(14),
                      }}
                    />
                  ))}
                </StyledStars>
                <Typography variant="caption" color="text.secondary">
                  {review.date}
                </Typography>
              </Box>

              {review.title && (
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {review.title}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: pxToRem(6), mb: 1 }}>
                <Typography variant="subtitle2">{review.author}</Typography>
                {review.verified && (
                  <>
                    <Icon
                      icon={faCircleCheck}
                      sx={{ color: 'success.main', fontSize: pxToRem(14) }}
                    />
                    <Typography variant="caption" color="success.main">
                      Verified Buyer
                    </Typography>
                  </>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                {review.content}
              </Typography>

              {review.photo && (
                <Box
                  component="img"
                  src={review.photo}
                  alt={`Review photo by ${review.author}`}
                  onClick={() => openLightbox(review.photo!)}
                  sx={{
                    mt: 1.5,
                    width: pxToRem(200),
                    height: 'auto',
                    borderRadius: pxToRem(8),
                    display: 'block',
                    cursor: 'zoom-in',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                />
              )}
            </Box>
          </React.Fragment>
        ))}
      </Box>

      {!expanded && reviews.featured_reviews.length > INITIAL_REVIEW_COUNT && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="text"
            onClick={() => setExpanded(true)}
          >
            Show All {reviews.featured_reviews.length} Reviews
          </Button>
        </Box>
      )}

      <ImageLightbox
        open={lightboxOpen}
        currentImageIndex={lightboxIndex}
        setCurrentIndex={setLightboxIndex}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
      />
    </StyledSection>
  )
}

const StyledSection = styled(Box)`
  padding: ${pxToRem(16)} 0 ${pxToRem(48)};
`

const StyledRatingSummary = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const StyledStars = styled(Box)`
  display: flex;
  gap: ${pxToRem(4)};
  margin: ${pxToRem(8)} 0;
`

const StyledRatingBars = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(8)};
`

const StyledRatingBarRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
`

const StyledRatingBar = styled(Box)`
  flex: 1;
  height: ${pxToRem(8)};
  background-color: ${(props) => props.theme.palette.divider};
  border-radius: ${pxToRem(4)};
  overflow: hidden;
`

const StyledRatingBarFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$percentage',
})<{ $percentage: number }>`
  height: 100%;
  width: ${(props) => props.$percentage}%;
  background-color: ${(props) => props.theme.palette.secondary.main};
  border-radius: ${pxToRem(4)};
  transition: width 0.3s ease;
`
